/**
 * "Add to calendar" after a booking and in the client area (boona
 * docs/codebase/google-calendar/README.md §9.7, plan GC1). The backend sends the links:
 * `meta.calendar` on POST /public/record/create and `calendar` on each upcoming visit of
 * GET /public/client/appointments, as {google, outlook, ics?}. The .ics is a file on the
 * salon's own API host and is never built here: a data: URI fails in iOS Chrome and in
 * in-app browsers.
 *
 * The order follows the device. Google's Android app cannot import an .ics, so Android
 * gets Google first. An iPhone, an iPad or a Mac opens the .ics in Apple's Calendar, so
 * there the file comes first and is called that. Anywhere else: Google, Outlook, then the
 * file for whatever calendar the computer has.
 */

/** The backend's block. `ics` is absent when the salon's API host is unknown. */
export type CalendarLinks = { google?: string; outlook?: string; ics?: string };

export type CalendarPlatform = "android" | "apple" | "other";

/** One link to show. `apple` and `other` are both the .ics, named for the device. */
export type CalendarChoice = { id: "google" | "outlook" | "apple" | "other"; url: string };

const KEYS = ["google", "outlook", "ics"] as const;

/**
 * The API's block, or undefined when it carries no usable link (a cancelled visit, a
 * backend from before GC1). Only http(s) links pass: nothing else may become an href.
 */
export function calendarLinksOf(value: unknown): CalendarLinks | undefined {
  if (!value || typeof value !== "object") return undefined;
  const out: CalendarLinks = {};
  for (const key of KEYS) {
    const url = (value as Record<string, unknown>)[key];
    if (typeof url === "string" && /^https?:\/\//i.test(url)) out[key] = url;
  }
  return Object.keys(out).length ? out : undefined;
}

/**
 * Android, Apple or anything else, from the user agent. iOS browsers all say "like Mac
 * OS X", and an iPad asking for the desktop site says "Macintosh": Apple either way.
 */
export function calendarPlatform(userAgent: string | undefined): CalendarPlatform {
  const ua = userAgent ?? "";
  if (/android/i.test(ua)) return "android";
  if (/iphone|ipad|ipod|macintosh|mac os x/i.test(ua)) return "apple";
  return "other";
}

/** The links in the order this device should see them. */
export function calendarChoices(
  links: CalendarLinks | null | undefined,
  platform: CalendarPlatform,
): CalendarChoice[] {
  if (!links) return [];
  const google: CalendarChoice | null = links.google ? { id: "google", url: links.google } : null;
  const outlook: CalendarChoice | null = links.outlook ? { id: "outlook", url: links.outlook } : null;
  const file: CalendarChoice | null = links.ics
    ? { id: platform === "apple" ? "apple" : "other", url: links.ics }
    : null;
  const order = platform === "apple" ? [file, google, outlook] : [google, outlook, file];
  return order.filter((c): c is CalendarChoice => c !== null);
}
